import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { router } from '@inertiajs/react';

// The Admin Hub (/admin, #1553) keeps its state here so embedded panels can
// read badges, switch tabs, or refresh counts without prop drilling.

export type HubTone = 'attention' | 'muted' | 'none';

export interface HubSub {
  key: string;
  label: string;
  href: string;
  tone: HubTone;
}

export interface HubSection {
  key: string;
  label: string;
  subs: HubSub[];
}

export interface HubCounts {
  tabs: Record<string, number>;
  attention_total: number;
}

export const OVERVIEW_TAB = 'overview';
export const COUNTS_URL = '/api/v1/admin/hub/counts';

interface AdminHubContextValue {
  sections: HubSection[];
  activeTab: string;
  activeSub: string | null;
  setTab: (tab: string, sub?: string | null) => void;
  counts: HubCounts | null;
  countsError: boolean;
  refreshCounts: (options?: { fresh?: boolean }) => Promise<void>;
  /** Count for a nested tab, undefined when it has no signal */
  countFor: (subKey: string) => number | undefined;
  /** Sum of orange (attention) counts in one section */
  attentionForSection: (sectionKey: string) => number;
  /** Sum of orange counts across every visible section */
  attentionTotal: number;
  /** Per-panel data cache for lazily loaded nested tabs */
  getOrFetch: <T>(key: string, fetcher: () => Promise<T>) => Promise<T>;
  /** Drops a cached key and all of its `?query` variants */
  invalidate: (key: string) => void;
}

const AdminHubContext = createContext<AdminHubContextValue | null>(null);

const readUrl = (sections: HubSection[]): { tab: string; sub: string | null } => {
  const params = new URLSearchParams(window.location.search);
  const section = sections.find((s) => s.key === params.get('tab'));
  if (!section) return { tab: OVERVIEW_TAB, sub: null };
  const sub = section.subs.find((s) => s.key === params.get('sub'));
  return { tab: section.key, sub: (sub ?? section.subs[0])?.key ?? null };
};

const writeUrl = (tab: string, sub: string | null) => {
  const url = new URL(window.location.href);
  if (tab === OVERVIEW_TAB) {
    url.searchParams.delete('tab');
    url.searchParams.delete('sub');
  } else {
    url.searchParams.set('tab', tab);
    if (sub) url.searchParams.set('sub', sub);
    else url.searchParams.delete('sub');
  }
  replaceUrlInPlace(url);
};

/**
 * Change the address bar without a request. A bare history.replaceState is
 * overwritten by Inertia, which writes its own copy of the page URL back
 * (e.g. when saving scroll), so the hub goes through Inertia's client-side visit.
 */
export const replaceUrlInPlace = (url: URL) => {
  router.replace({ url: `${url.pathname}${url.search}${url.hash}`, preserveState: true, preserveScroll: true });
};

export const fetchHubCounts = async (fresh = false): Promise<HubCounts> => {
  const response = await fetch(`${COUNTS_URL}${fresh ? '?fresh=1' : ''}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Could not load admin counts');
  return data as HubCounts;
};

export const AdminHubProvider: React.FC<{ sections: HubSection[]; children: ReactNode }> = ({ sections, children }) => {
  const initial = useMemo(() => readUrl(sections), [sections]);
  const [activeTab, setActiveTab] = useState(initial.tab);
  const [activeSub, setActiveSub] = useState<string | null>(initial.sub);
  const [counts, setCounts] = useState<HubCounts | null>(null);
  const [countsError, setCountsError] = useState(false);
  const cache = useRef(new Map<string, Promise<unknown>>());

  const setTab = useCallback(
    (tab: string, sub?: string | null) => {
      const section = sections.find((s) => s.key === tab);
      const nextTab = section ? tab : OVERVIEW_TAB;
      const nextSub = section ? (sub && section.subs.some((s) => s.key === sub) ? sub : section.subs[0]?.key ?? null) : null;
      setActiveTab(nextTab);
      setActiveSub(nextSub);
      writeUrl(nextTab, nextSub);
    },
    [sections],
  );

  const refreshCounts = useCallback(async ({ fresh = false } = {}) => {
    try {
      setCounts(await fetchHubCounts(fresh));
      setCountsError(false);
    } catch {
      setCountsError(true);
    }
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const countFor = useCallback((subKey: string) => counts?.tabs[subKey], [counts]);

  const attentionForSection = useCallback(
    (sectionKey: string) => {
      const section = sections.find((s) => s.key === sectionKey);
      if (!section || !counts) return 0;
      return section.subs
        .filter((sub) => sub.tone === 'attention')
        .reduce((sum, sub) => sum + (counts.tabs[sub.key] ?? 0), 0);
    },
    [sections, counts],
  );

  const attentionTotal = useMemo(
    () => sections.reduce((sum, section) => sum + attentionForSection(section.key), 0),
    [sections, attentionForSection],
  );

  const getOrFetch = useCallback(<T,>(key: string, fetcher: () => Promise<T>): Promise<T> => {
    const cached = cache.current.get(key);
    if (cached) return cached as Promise<T>;
    const pending = fetcher().catch((error) => {
      // Failed loads are not cached, so Retry fetches again.
      cache.current.delete(key);
      throw error;
    });
    cache.current.set(key, pending);
    return pending;
  }, []);

  // Drops the key and every query-string variant of it, so invalidating
  // '/admin/claims.json' also clears its cached filter/page combinations.
  const invalidate = useCallback((key: string) => {
    Array.from(cache.current.keys())
      .filter((cached) => cached === key || cached.startsWith(`${key}?`))
      .forEach((cached) => cache.current.delete(cached));
  }, []);

  const value = useMemo(
    () => ({
      sections,
      activeTab,
      activeSub,
      setTab,
      counts,
      countsError,
      refreshCounts,
      countFor,
      attentionForSection,
      attentionTotal,
      getOrFetch,
      invalidate,
    }),
    [sections, activeTab, activeSub, setTab, counts, countsError, refreshCounts, countFor, attentionForSection, attentionTotal, getOrFetch, invalidate],
  );

  return <AdminHubContext.Provider value={value}>{children}</AdminHubContext.Provider>;
};

export const useAdminHub = () => {
  const context = useContext(AdminHubContext);
  if (!context) throw new Error('useAdminHub must be used within an AdminHubProvider');
  return context;
};
